import i18next from 'i18next';
import { T } from '../styles/tokens';
import { formatTime, formatDate, formatTimeAgo } from '../utils/time';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { signal, effect, Signal, computed } from '@preact/signals';
import { route } from 'preact-router';
import { api } from '../api/client';
import { isLoggedIn, hasActivePlan, isAdmin, user } from '../stores/auth.store';
import { NoPlanGate } from '../components/NoPlanGate';
import { tenantName } from '../components/TenantLogo';
import { Button, Modal, Input, ListSkeleton, SkeletonBar, Spinner, ChatBubble, TypingIndicator, OnlineDot, SlotsRemainingBadge, CountdownTimer, ModelSelector, AttachMenu, IconButton, Icon, PromoBanner, RechargeModal as UIRechargeModal } from '../components/ui';
import { renderContent } from '../utils/renderContent';
import { CHAT_COLORS, FILE_EXTENSION_COLORS, FILE_EXTENSION_DEFAULT_COLOR } from '../constants/chat';
import { toolMeta } from '../constants/tools';
import { useChatSounds } from '../hooks/useChatSounds';
import { fileToDataUrl } from '../utils/files';

// ---- Types ----

interface Conversation {
  id: string;
  type: 'elon_chat' | 'openclaw';
  workspace_id?: string;
  agent_id?: string;
  title?: string;
  model?: string;
  last_message_at?: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface HubMessage {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  attachments?: string;
  tool_data?: string;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_usd?: number;
  duration_ms?: number;
  created_at: string;
}

interface Agent {
  id: string;
  name: string;
  type: 'elon_chat' | 'openclaw';
  status: 'online' | 'offline';
  description?: string;
  workspace_url?: string;
}

interface LLMModel {
  id: string;
  name: string;
  provider: string;
}

// ---- State ----

// Cache síncrono — carrega instantâneo antes de qualquer fetch
const _cachedConvs = (() => { try { return JSON.parse(localStorage.getItem('chathub_convs_cache') || '[]'); } catch { return []; } })();
const _cachedAgents = (() => { try { return JSON.parse(localStorage.getItem('chathub_agents_cache') || '[]'); } catch { return []; } })();

const conversations = signal<Conversation[]>(_cachedConvs);
const activeConv = signal<Conversation | null>(null);
const msgs = signal<HubMessage[]>([]);
const agents = signal<Agent[]>(_cachedAgents);
const models = signal<LLMModel[]>([]);
const balance = signal<number | null>(null);
const inputText = signal('');
const sending = signal(false);
const loadingMsgs = signal(false);
const loadingConvs = signal(_cachedConvs.length === 0);
const loadingAgents = signal(_cachedAgents.length === 0);
const sidebarOpen = signal(window.innerWidth >= 768);
const selectedAgent = signal<Agent | null>(_cachedAgents[0] || null);
const selectedModel = signal('auto');
const modelDropdownOpen = signal(false);
const imageModal = signal<string | null>(null);
const showActions = signal(localStorage.getItem('chathub_showActions') !== 'false');
const chatSoundsEnabled = signal(localStorage.getItem('chathub_sounds') === 'true');
const autoReadEnabled = signal(localStorage.getItem('chathub_autoRead') === 'true');
const pendingImages = signal<Array<{ file: File; dataUrl: string }>>([]);
const attachMenuOpen = signal(false);
const dragOver = signal(false);
const searchQuery = signal('');
const showRechargeModal = signal(false);

// ---- Typewriter greeting ----
const GREETING_PHRASES = () => [
  i18next.t('chat:greeting_1', { defaultValue: 'Como posso ajudar?' }),
  i18next.t('chat:greeting_6', { defaultValue: 'O que você quer criar hoje?' }),
  i18next.t('chat:greeting_2', { defaultValue: 'Precisa de algo?' }),
  i18next.t('chat:greeting_3', { defaultValue: 'No que posso ajudar?' }),
  i18next.t('chat:greeting_4', { defaultValue: 'Qual é a ideia de hoje?' }),
  i18next.t('chat:greeting_5', { defaultValue: 'Vamos construir algo?' }),
];

function useTypewriter() {
  const [display, setDisplay] = useState('');
  const phraseIdx = useRef(0);
  const charIdx = useRef(0);
  const deleting = useRef(false);
  const timer = useRef<any>(null);

  useEffect(() => {
    const tick = () => {
      const phrases = GREETING_PHRASES();
      const phrase = phrases[phraseIdx.current];
      if (!deleting.current) {
        // Typing
        charIdx.current++;
        setDisplay(phrase.slice(0, charIdx.current));
        if (charIdx.current >= phrase.length) {
          // Pause before deleting
          timer.current = setTimeout(() => { deleting.current = true; tick(); }, 7000);
          return;
        }
        timer.current = setTimeout(tick, 45 + Math.random() * 35);
      } else {
        // Deleting
        charIdx.current--;
        setDisplay(phrase.slice(0, charIdx.current));
        if (charIdx.current <= 0) {
          deleting.current = false;
          phraseIdx.current = (phraseIdx.current + 1) % phrases.length;
          timer.current = setTimeout(tick, 400);
          return;
        }
        timer.current = setTimeout(tick, 25);
      }
    };
    tick();
    return () => clearTimeout(timer.current);
  }, []);

  return display;
}

// Signal de sessões ativas (usado pela Sidebar)
export const activeSessions = signal<any[]>([]);
export const loadingSessions = signal(false);

export async function fetchSessions(wsId: string) {
  // Só mostrar loading na primeira carga (quando não tem dados ainda)
  const isFirst = activeSessions.value.length === 0;
  if (isFirst) loadingSessions.value = true;
  try {
    const res = await api.get<any>(`/chat/sessions/${wsId}`);
    activeSessions.value = res?.sessions || [];
  } catch {
    // Não limpar sessões existentes em caso de erro (evita flash)
    if (isFirst) activeSessions.value = [];
  } finally {
    if (isFirst) loadingSessions.value = false;
  }
}

// Exportar signals para sidebar unificada mobile + header mobile (model selector)
export { agents, conversations, searchQuery, selectedAgent, activeConv, sidebarOpen, loadingConvs, loadingAgents, balance, msgs, models, selectedModel, modelDropdownOpen };

// Computed signals for sidebar rendering
const onlineAgentsComputed = computed(() => agents.value.filter(a => a.status === 'online'));
const filteredConvsComputed = computed(() => {
  const q = searchQuery.value.toLowerCase();
  return q ? conversations.value.filter(c => (c.title || '').toLowerCase().includes(q)) : conversations.value;
});

// Voice state
const isListening = signal(false);
const isJarvisMode = signal(false);
const isSpeaking = signal(false);
const voiceTranscript = signal('');

// Push
const pushState = signal<'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported'>('unknown');

// Sons de chat via hook centralizado
const { playSendSound, playReceiveSound } = useChatSounds(chatSoundsEnabled);

// ---- Voice / TTS ----
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
let recognition: any = null;
let speechSynth = window.speechSynthesis;
let jarvisIdleTimer: any = null;
let lastSpeechTime = 0;

function speakText(text: string): Promise<void> {
  return new Promise((resolve) => {
    const clean = text.replace(/```[\s\S]*?```/g, ' codigo omitido ').replace(/`[^`]+`/g, (m) => m.slice(1, -1))
      .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').replace(/#{1,6}\s/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/[_~]/g, '').replace(/<[^>]+>/g, '').replace(/\n+/g, '. ').trim();
    if (!clean) { resolve(); return; }
    speechSynth.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = 'pt-BR'; u.rate = 1.1;
    const voices = speechSynth.getVoices();
    const ptVoice = voices.find((v: any) => v.lang.startsWith('pt')) || voices[0];
    if (ptVoice) u.voice = ptVoice;
    isSpeaking.value = true;
    u.onend = () => { isSpeaking.value = false; resolve(); };
    u.onerror = () => { isSpeaking.value = false; resolve(); };
    speechSynth.speak(u);
  });
}

function stopSpeaking() { speechSynth.cancel(); isSpeaking.value = false; }

function startListening(jarvis = false) {
  if (!SpeechRecognition) { alert('Seu navegador nao suporta reconhecimento de voz.'); return; }
  if (recognition) { recognition.abort(); recognition = null; }
  recognition = new SpeechRecognition();
  recognition.lang = 'pt-BR'; recognition.interimResults = true; recognition.continuous = jarvis;
  recognition.onstart = () => { isListening.value = true; voiceTranscript.value = ''; };
  recognition.onresult = (event: any) => {
    let interim = '', final = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) final += t; else interim += t;
    }
    voiceTranscript.value = final || interim;
    lastSpeechTime = Date.now();
    if (final && !jarvis) { /* will be handled in onend */ }
    if (final && jarvis && final.trim()) {
      voiceTranscript.value = '';
      inputText.value = final.trim();
      handleSend();
    }
  };
  recognition.onend = () => {
    if (!jarvis) {
      const t = voiceTranscript.value.trim();
      isListening.value = false; voiceTranscript.value = '';
      if (t) { inputText.value = t; handleSend(); }
    } else if (isJarvisMode.value && !isSpeaking.value) {
      setTimeout(() => { if (isJarvisMode.value && recognition) try { recognition.start(); } catch {} }, 300);
    } else { isListening.value = false; voiceTranscript.value = ''; }
  };
  recognition.onerror = (e: any) => {
    if (e.error === 'not-allowed') { alert('Permissao de microfone negada.'); isListening.value = false; isJarvisMode.value = false; return; }
    if (jarvis && isJarvisMode.value && (e.error === 'no-speech' || e.error === 'aborted')) return;
    isListening.value = false; isJarvisMode.value = false; voiceTranscript.value = '';
  };
  if (jarvis) isJarvisMode.value = true;
  recognition.start();
}

function stopListening() {
  isJarvisMode.value = false; isListening.value = false; voiceTranscript.value = '';
  if (jarvisIdleTimer) clearTimeout(jarvisIdleTimer);
  stopSpeaking();
  if (recognition) { recognition.abort(); recognition = null; }
}

// ---- Helpers ----
// Movido para utils/time.ts

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// toolMeta importado de constants/tools.ts

async function addImages(files: FileList | File[]) {
  for (const file of Array.from(files)) {
    if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_IMAGE_SIZE) continue;
    if (pendingImages.value.length >= 4) break;
    const dataUrl = await fileToDataUrl(file);
    pendingImages.value = [...pendingImages.value, { file, dataUrl }];
  }
}

// ---- API ----

/** Carrega tudo em 1 request: agents + conversations + balance */
export async function fetchInit() {
  try {
    const res = await api.get<any>('/chat/init');
    const convList = res?.conversations || [];
    const agentList = res?.agents || [];
    conversations.value = convList;
    agents.value = agentList;
    if (!selectedAgent.value && agentList.length > 0) selectedAgent.value = agentList[0] || null;
    walletBalance.value = res?.balance ?? 0;

    // Cachear
    try {
      localStorage.setItem('chathub_convs_cache', JSON.stringify(convList));
      localStorage.setItem('chathub_agents_cache', JSON.stringify(agentList));
    } catch {}
  } catch {} finally { loadingConvs.value = false; loadingAgents.value = false; }
}

export async function fetchConversations() {
  try {
    const res = await api.get<any>('/chat/conversations');
    conversations.value = res?.conversations || [];
    try { localStorage.setItem('chathub_convs_cache', JSON.stringify(conversations.value)); } catch {}
  } catch {} finally { loadingConvs.value = false; }
}

export async function fetchAgents() {
  const isFirst = agents.value.length === 0;
  if (isFirst) loadingAgents.value = true;
  try {
    const res = await api.get<any>('/chat/agents');
    agents.value = res?.agents || [];
    if (!selectedAgent.value && agents.value.length > 0) selectedAgent.value = agents.value[0] || null;
    try { localStorage.setItem('chathub_agents_cache', JSON.stringify(agents.value)); } catch {}
  } catch {} finally {
    loadingAgents.value = false;
  }
}

async function fetchModels() {
  try {
    const res = await api.get<any>('/chat/models');
    models.value = res?.models || [];
    // Usar modelo default da config (se definido e diferente de auto)
    if (res?.default_model && res.default_model !== 'auto' && selectedModel.value === 'auto') {
      selectedModel.value = res.default_model;
    }
  } catch {}
}

async function fetchBalance() {
  try {
    const res = await api.get<any>('/chat/balance');
    balance.value = res?.balance ?? null;
  } catch {}
}

export async function fetchMessages(convId: string) {
  // Só mostrar loading se não tem mensagens ainda (evita flicker/piscada)
  if (msgs.value.length === 0) loadingMsgs.value = true;
  try {
    // Sessões virtuais — buscar histórico via Gateway
    if (convId.startsWith('_session_')) {
      const conv = conversations.value.find(c => c.id === convId) as any;
      if (conv?._sessionKey && conv?.workspace_id) {
        const res = await api.get<any>(`/chat/session-history/${conv.workspace_id}/${conv._sessionKey}`);
        const history = res?.messages || [];
        // Converter pra formato HubMessage
        msgs.value = history.map((m: any, i: number) => ({
          id: `sh_${i}`,
          conversation_id: convId,
          role: m.role || 'user',
          content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
          model: m.model,
          tokens_in: m.inputTokens,
          tokens_out: m.outputTokens,
          created_at: m.timestamp || new Date().toISOString(),
        }));
        return;
      }
    }
    const res = await api.get<any>(`/chat/conversations/${convId}/messages`);
    msgs.value = res?.messages || [];
    if (res?.conversation) {
      activeConv.value = res.conversation;
    }
  } catch {} finally { loadingMsgs.value = false; }
}

async function createConversation(agentOrType: Agent | null, model?: string): Promise<Conversation | null> {
  const type = agentOrType?.type || 'elon_chat';
  const workspace_id = agentOrType?.type === 'openclaw' ? agentOrType.id : undefined;
  try {
    const res = await api.post<any>('/chat/conversations', { type, workspace_id, model: model || selectedModel.value });
    const conv = res?.conversation;
    if (conv) {
      conversations.value = [conv, ...conversations.value];
      return conv;
    }
  } catch {}
  return null;
}

async function handleSend() {
  const text = inputText.value.trim();
  const images = pendingImages.value;
  if ((!text && images.length === 0) || sending.value) return;

  inputText.value = '';
  pendingImages.value = [];
  const ta = document.querySelector('[data-chathub-input]') as HTMLTextAreaElement;
  if (ta) ta.style.height = 'auto';
  sending.value = true;
  playSendSound();

  // Garantir que temos uma conversa — se não tem, criar e redirecionar
  let conv = activeConv.value;
  if (!conv) {
    conv = await createConversation(selectedAgent.value, selectedModel.value);
    if (!conv) { sending.value = false; return; }
    activeConv.value = conv;
    // Redirecionar pra URL da conversa (padrão ChatGPT)
    route(`/chat/${conv.id}`);
  }

  // Optimistic user message
  const optId = `opt_${Date.now()}`;
  msgs.value = [...msgs.value, {
    id: optId, conversation_id: conv.id, role: 'user', content: text || 'Imagem',
    created_at: new Date().toISOString(),
    attachments: images.length > 0 ? JSON.stringify(images.map(img => ({ type: 'image', mimeType: img.file.type, dataUrl: img.dataUrl }))) : undefined,
  }];

  try {
    if (conv.type === 'elon_chat') {
      // Streaming SSE — resposta palavra por palavra
      const baseUrl = '/api/v1';
      const fetchRes = await fetch(`${baseUrl}/chat/conversations/${conv.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text || '(imagem)',
          model: selectedModel.value,
          attachments: images.length > 0 ? images.map(img => ({ type: 'image', mimeType: img.file.type, dataUrl: img.dataUrl })) : undefined,
        }),
      });

      const resContentType = fetchRes.headers.get('content-type') || '';

      // Helper: mapear erros HTTP/texto para mensagens amigáveis
      const _friendlyError = (status: number, text: string): string => {
        if (status === 429) return '⚠️ Muitas requisições. Aguarde alguns minutos.';
        const lower = text.toLowerCase();
        if (lower.includes('api key') || lower.includes('sem chave') || lower.includes('no key') || lower.includes('not configured'))
          return '⚠️ Chave de API não configurada. Configure em /profile/elon-router';
        if (lower.includes('insufficient') || lower.includes('saldo') || lower.includes('balance'))
          return '⚠️ Saldo insuficiente na carteira. Adicione créditos em /profile/wallet';
        if (lower.includes('timeout') || lower.includes('timed out'))
          return '⚠️ Resposta demorou muito. Tente novamente.';
        return text || '⚠️ Erro ao processar mensagem. Tente novamente.';
      };

      // Verificar erro HTTP ANTES de tentar ler stream
      if (!fetchRes.ok && !resContentType.includes('text/event-stream')) {
        const errText = await fetchRes.text().catch(() => 'Erro desconhecido');
        let errMsg = _friendlyError(fetchRes.status, errText);
        try { const parsed = JSON.parse(errText); errMsg = _friendlyError(fetchRes.status, parsed?.error?.message || errText); } catch {}
        msgs.value = [...msgs.value.filter(m => m.id !== optId), {
          id: `err_${Date.now()}`, conversation_id: conv?.id || '', role: 'assistant' as const,
          content: errMsg, created_at: new Date().toISOString(),
        }];
      } else if (resContentType.includes('text/event-stream') && fetchRes.body) {
        // Modo streaming SSE — ler chunks e atualizar em tempo real
        const reader = fetchRes.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let sseBuffer = '';
        const assistantId = `stream_${Date.now()}`;
        let userMessage: HubMessage | null = null;
        let streamRafPending = false;

        // Criar placeholder da mensagem do assistente
        msgs.value = [...msgs.value, {
          id: assistantId, conversation_id: conv.id, role: 'assistant',
          content: '', created_at: new Date().toISOString(),
        }];

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            sseBuffer += decoder.decode(value, { stream: true });
            const lines = sseBuffer.split('\n');
            sseBuffer = lines.pop() || '';

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed || trimmed.startsWith(':')) continue;
              if (trimmed === 'data: [DONE]') continue;
              if (!trimmed.startsWith('data: ')) continue;

              try {
                const json = JSON.parse(trimmed.slice(6));

                // Evento de erro do servidor via SSE
                if (json.error) {
                  fullContent = _friendlyError(json.error.status || 500, json.error.message || '');
                  continue;
                }

                // Evento meta com dados da mensagem do usuário
                if (json.type === 'meta' && json.user_message) {
                  userMessage = json.user_message;
                  // Substituir mensagem otimista pela real
                  msgs.value = msgs.value.map(m => m.id === optId ? json.user_message : m);
                  continue;
                }

                // Chunk SSE do LLM — acumular conteúdo
                const delta = json?.choices?.[0]?.delta;
                let needsUpdate = false;
                if (delta?.content) {
                  fullContent += delta.content;
                  needsUpdate = true;
                }
                if (json?.model) { needsUpdate = true; }
                if (json?.usage) { needsUpdate = true; }

                // Batch update: acumular mudanças e renderizar no próximo frame
                if (needsUpdate) {
                  const _model = json?.model;
                  const _usage = json?.usage;
                  if (!streamRafPending) {
                    streamRafPending = true;
                    requestAnimationFrame(() => {
                      streamRafPending = false;
                      msgs.value = msgs.value.map(m => {
                        if (m.id !== assistantId) return m;
                        const updated = { ...m, content: fullContent };
                        if (_model) updated.model = _model;
                        if (_usage) {
                          updated.tokens_in = _usage.prompt_tokens;
                          updated.tokens_out = _usage.completion_tokens;
                          updated.cost_usd = _usage.cost_usd;
                        }
                        return updated;
                      });
                    });
                  }
                }
              } catch { /* chunk inválido */ }
            }
          }
        } catch (streamErr: any) {
          console.error('Stream read error:', streamErr);
          if (!fullContent.trim()) {
            fullContent = _friendlyError(0, streamErr?.message || '');
          }
        }

        // Se stream terminou vazio = erro silencioso do servidor
        if (!fullContent.trim()) {
          fullContent = !fetchRes.ok
            ? _friendlyError(fetchRes.status, '')
            : '⚠️ Erro ao processar mensagem. Tente novamente.';
        }

        // Flush final — garantir que o último frame renderiza
        msgs.value = msgs.value.map(m =>
          m.id === assistantId ? { ...m, content: fullContent } : m
        );
        // NÃO recarregar msgs — já temos os dados do stream (evita flicker/piscada)
        playReceiveSound();
        if (autoReadEnabled.value && fullContent) speakText(fullContent);
      } else if (!fetchRes.ok) {
        // Erro HTTP — mostrar mensagem de erro no chat
        const errText = await fetchRes.text().catch(() => 'Erro desconhecido');
        let errMsg = 'Erro ao processar mensagem.';
        try { const parsed = JSON.parse(errText); errMsg = parsed?.error?.message || errMsg; } catch {}
        msgs.value = msgs.value.map(m =>
          m.id === optId ? { ...m, content: errMsg, role: 'assistant' as const } : m
        ).filter(m => m.id !== optId);
        msgs.value = [...msgs.value, {
          id: `err_${Date.now()}`, conversation_id: conv?.id || '', role: 'assistant' as const,
          content: errMsg, created_at: new Date().toISOString(),
        }];
      } else {
        // Fallback: resposta JSON normal (não-streaming)
        const res = await fetchRes.json().then((d: any) => d?.data || d);
        const assistantMsg = res?.assistant_message;
        if (assistantMsg) {
          msgs.value = [...msgs.value.filter(m => m.id !== optId), res?.user_message, assistantMsg];
          playReceiveSound();
          if (autoReadEnabled.value) speakText(assistantMsg.content);
          if (res?.meta?.balance_after != null) balance.value = res.meta.balance_after;
        }
      }
      // Atualizar lista de conversas (título pode ter mudado) — debounce pra não piscar
      setTimeout(() => fetchConversations(), 1000);
    } else if (conv.type === 'openclaw' && conv.workspace_id) {
      // Streaming SSE — proxy direto pro OpenClaw Gateway
      const baseUrl = '/api/v1';
      const assistantId = `stream_${Date.now()}`;

      // Mostrar placeholder "pensando" imediatamente
      msgs.value = [...msgs.value, {
        id: assistantId, conversation_id: conv.id, role: 'assistant',
        content: '', created_at: new Date().toISOString(),
      }];

      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 180000); // 3 min timeout

      try {
        const fetchRes = await fetch(`${baseUrl}/chat/proxy/${conv.workspace_id}/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            message: text, conversation_id: conv.id, model: selectedModel.value, stream: true,
          }),
        });

        clearTimeout(fetchTimeout);
        const resContentType = fetchRes.headers.get('content-type') || '';

        if (resContentType.includes('text/event-stream') && fetchRes.body) {
          // Modo streaming SSE — ler chunks em tempo real (com rAF throttle)
          const reader = fetchRes.body.getReader();
          const decoder = new TextDecoder();
          let fullContent = '';
          let sseBuffer = '';
          let ocRafPending = false;
          let lastModel: string | undefined;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            sseBuffer += decoder.decode(value, { stream: true });
            const lines = sseBuffer.split('\n');
            sseBuffer = lines.pop() || '';

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed || trimmed.startsWith(':') || trimmed === 'data: [DONE]') continue;
              if (!trimmed.startsWith('data: ')) continue;

              try {
                const json = JSON.parse(trimmed.slice(6));
                const delta = json?.choices?.[0]?.delta;
                if (delta?.content) fullContent += delta.content;
                if (json?.model) lastModel = json.model;

                if (!ocRafPending) {
                  ocRafPending = true;
                  requestAnimationFrame(() => {
                    ocRafPending = false;
                    msgs.value = msgs.value.map(m => {
                      if (m.id !== assistantId) return m;
                      const updated = { ...m, content: fullContent };
                      if (lastModel) updated.model = lastModel;
                      return updated;
                    });
                  });
                }
              } catch { /* chunk inválido */ }
            }
          }

          // Flush final (garantir que o último frame renderiza)
          if (!fullContent.trim()) {
            // Stream vazio = erro interno do agente (ex: saldo insuficiente)
            fullContent = '⚠️ O agente não conseguiu responder. Verifique se sua carteira tem saldo suficiente ou tente novamente.';
          }
          msgs.value = msgs.value.map(m =>
            m.id === assistantId ? { ...m, content: fullContent, model: lastModel || m.model } : m
          );
          playReceiveSound();
          if (autoReadEnabled.value && fullContent) speakText(fullContent);
        } else if (!fetchRes.ok) {
          // Erro HTTP — mostrar na mensagem
          const errText = await fetchRes.text().catch(() => 'Erro desconhecido');
          let errMsg = 'Erro ao conectar com o agente.';
          try { const parsed = JSON.parse(errText); errMsg = parsed?.error?.message || errMsg; } catch {}
          msgs.value = msgs.value.map(m =>
            m.id === assistantId ? { ...m, content: errMsg } : m
          );
        } else {
          // Fallback: resposta JSON não-streaming
          const res = await fetchRes.json().then((d: any) => d?.data || d);
          const content = res?.content || '';
          msgs.value = msgs.value.map(m =>
            m.id === assistantId ? { ...m, content, model: res?.model } : m
          );
          playReceiveSound();
          if (autoReadEnabled.value) speakText(content);
        }
      } catch (fetchErr: any) {
        clearTimeout(fetchTimeout);
        const isAbort = fetchErr?.name === 'AbortError';
        msgs.value = msgs.value.map(m =>
          m.id === assistantId ? { ...m, content: isAbort ? 'Tempo limite excedido. Tente novamente.' : `Erro: ${fetchErr?.message || 'Falha na conexão'}` } : m
        );
      }
      fetchConversations();
    }
  } catch (err: any) {
    console.error('Send failed:', err);
  } finally { sending.value = false; }
}

async function archiveConversation(convId: string) {
  try {
    await api.delete(`/chat/conversations/${convId}`);
    conversations.value = conversations.value.filter(c => c.id !== convId);
    if (activeConv.value?.id === convId) { route('/chat'); }
  } catch {}
}

async function subscribeToPushIfNeeded() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return;
    const vapidRes = await api.get('/notifications/vapid-key').catch(() => null) as any;
    const vapidKey = vapidRes?.data?.publicKey || vapidRes?.publicKey;
    if (!vapidKey) return;
    const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
    const b64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const arr = new Uint8Array(Array.from(raw).map(c => c.charCodeAt(0)));
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
    const json = sub.toJSON();
    await api.post('/notifications/subscribe', { endpoint: json.endpoint, keys: json.keys }).catch(() => {});
  } catch {}
}

// ---- Colors / Design ----
const COLORS = {
  bgPage: T.bg,
  bgSidebar: T.bg,
  bgChat: T.bg,
  bgGlass: T.border,
  userBubble: `linear-gradient(135deg, ${T.primary}, ${T.accentBg})`,
  assistantBubble: T.bgCard,
  textPrimary: T.text,
  textMuted: T.textSecondary,
  accentPrimary: T.primary,
  accentGreen: T.success,
  border: T.bgHover,
};

// ---- Component ----

// ── Banner Promocional Modal ──
// Usa PromoBanner de ../components/ui

function PromoBannerModal() {
  return (
    <PromoBanner
      isLoggedIn={isLoggedIn}
      fetchBanners={() => api.get<any>('/promotions/banners?timing=after_login').then((res: any) => res?.data?.banners || res?.banners || [])}
      dismissBanner={(viewId) => api.post(`/promotions/banners/${viewId}/dismiss`, {})}
    />
  );
}


/* ══ AGENT LANDING PAGE ══ */
function AgentLandingView({ agent, inputText, pendingImages, addImages, fileInputRef, attachMenuOpen, attachBtnRef, sending, handleSend, isListening, startListening, recognition, models, selectedModel, modelDropdownOpen }: {
  agent: Agent; inputText: Signal<string>; pendingImages: Signal<any[]>; addImages: (f: FileList) => void;
  fileInputRef: any; attachMenuOpen: Signal<boolean>; attachBtnRef: any; sending: Signal<boolean>;
  handleSend: () => void; isListening: Signal<boolean>; startListening: (b: boolean) => void; recognition: any;
  models: Signal<any[]>; selectedModel: Signal<any>; modelDropdownOpen: Signal<boolean>;
}) {
  const isOnline = agent.status === 'online';
  const isMobile = window.innerWidth < 768;

  const starters = [
    { emoji: '💬', label: 'Iniciar conversa', prompt: `Olá ${agent.name}! ` },
    { emoji: '❓', label: 'Fazer uma pergunta', prompt: 'Me ajude com ' },
    { emoji: '📝', label: 'Criar conteúdo', prompt: 'Escreva ' },
    { emoji: '🔍', label: 'Pesquisar algo', prompt: 'Pesquise sobre ' },
  ];

  const handleStarter = (prompt: string) => {
    inputText.value = prompt;
    const ta = document.querySelector('[data-chathub-input]') as HTMLTextAreaElement;
    if (ta) { ta.focus(); ta.setSelectionRange(prompt.length, prompt.length); }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.bg, padding: '0 16px' }}>
      {/* Avatar */}
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: T.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, marginBottom: 16, border: `2px solid ${T.border}` }}>
        {agent.type === 'openclaw' ? '🤖' : '⚡'}
      </div>

      {/* Nome */}
      <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, margin: '0 0 6px 0' }}>{agent.name}</h1>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: T.textSecondary, marginBottom: 12 }}>
        <span>{agent.type === 'openclaw' ? 'Automático' : 'Chat'}</span>
        <span>·</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#22c55e' : '#ef4444', display: 'inline-block' }} />
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Descrição */}
      <p style={{ fontSize: 15, color: T.textMuted, textAlign: 'center', maxWidth: 400, margin: '0 0 28px 0', lineHeight: 1.5 }}>
        {agent.description || `Converse com ${agent.name} — seu assistente inteligente`}
      </p>

      {/* Conversation Starters — Grid 2x2 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, maxWidth: 460, width: '100%', marginBottom: 28 }}>
        {starters.map(s => (
          <div key={s.label}
            onClick={() => handleStarter(s.prompt)}
            style={{ padding: '14px 16px', borderRadius: 16, border: `1px solid ${T.borderHover}`, background: 'transparent', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 10 }}
            onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
            onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ fontSize: 20 }}>{s.emoji}</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Input bar */}
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : 860 }}>
        {pendingImages.value.length > 0 && (
          <div style={{ padding: '8px 12px', background: COLORS.bgSidebar, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', gap: 8, marginBottom: 8, borderRadius: 12 }}>
            {pendingImages.value.map((img: any, idx: number) => (
              <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                <img src={img.dataUrl} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `2px solid ${COLORS.border}` }} />
                <Button variant="ghost" onClick={() => { pendingImages.value = pendingImages.value.filter((_: any, i: number) => i !== idx); }}
                  style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%', background: T.danger, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.text }}>x</Button>
              </div>
            ))}
          </div>
        )}
        <div style={{ background: T.bgInput, borderRadius: 24, position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 8px 4px', gap: 4 }}>
            <AttachMenu menuOpen={attachMenuOpen} fileInputRef={fileInputRef} btnRef={attachBtnRef} />
            <textarea
              data-chathub-input
              placeholder="Pergunte alguma coisa"
              value={inputText.value}
              onFocus={(e: any) => { setTimeout(() => { e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 300); }}
              onInput={(e: any) => {
                inputText.value = e.target.value;
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e: KeyboardEvent) => {
                const mob = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
                if (e.key === 'Enter' && !e.shiftKey && !mob) { e.preventDefault(); handleSend(); }
              }}
              disabled={sending.value}
              rows={1}
              style={{
                flex: 1, padding: '0 8px', borderRadius: 0,
                background: 'transparent', border: 'none',
                fontSize: 16, lineHeight: 1.5, color: T.text,
                fontFamily: 'inherit', outline: 'none', resize: 'none',
                maxHeight: 120, overflowY: 'auto',
              }}
            />
            {window.innerWidth >= 768 && (
              <ModelSelector models={models} selectedModel={selectedModel} dropdownOpen={modelDropdownOpen} />
            )}
            <Button variant="ghost" onClick={() => { if (!isListening.value) startListening(false); else if (recognition) recognition.stop(); }}
              class="chathub-mic-btn"
              style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', display: window.innerWidth > 768 ? 'flex' : (!inputText.value.trim() ? 'flex' : 'none'), alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: isListening.value ? T.danger : T.textSecondary, padding: 0 }}
              onMouseEnter={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.text; }}
              onMouseLeave={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.textSecondary; }}>
              <Icon name="microphone" size={18} />
            </Button>
            <Button variant="ghost" data-chathub-send onClick={handleSend}
              disabled={(!inputText.value.trim() && pendingImages.value.length === 0) || sending.value}
              style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', padding: 0, background: (inputText.value.trim() || pendingImages.value.length > 0) && !sending.value ? T.text : T.textMuted, transition: 'all 0.2s' }}>
              <Icon name="arrow-up" size={14} color="#000" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatHubPageInner({ id, agentId }: { id?: string; agentId?: string }) {
  // if (!isAdmin.value && !hasActivePlan.value) return <NoPlanGate />;

  const chatAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachBtnRef = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const isNearBottom = useRef(true);
  const prevLen = useRef(0);
  const dragCounter = useRef(0);
  const greetingText = useTypewriter();

  // Local state for sidebar data (signals don't trigger re-renders reliably)
  const [localAgents, setLocalAgents] = useState<Agent[]>([]);
  const [localConvs, setLocalConvs] = useState<Conversation[]>([]);

  // Override main-content for fullscreen chat
  useEffect(() => {
    const main = document.querySelector('.main-content') as HTMLElement;
    if (!main) return;
    const saved = { p: main.style.padding, o: main.style.overflow, oy: main.style.overflowY };
    main.classList.add('chat-active');
    main.style.padding = '0'; main.style.overflow = 'hidden'; main.style.overflowY = 'hidden';

    return () => {
      main.classList.remove('chat-active');
      main.style.padding = saved.p; main.style.overflow = saved.o; main.style.overflowY = saved.oy;
    };
  }, []);

  // Init data — 1 request pra tudo (agents + conversations + balance)
  useEffect(() => {
    fetchInit().then(() => { setLocalAgents(agents.value); setLocalConvs(conversations.value); });
    fetchModels(); // models separado (chama Router, mais lento)
    const iv = setInterval(fetchBalance, 60000);

    // Push notifications
    (async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) { pushState.value = 'unsupported'; return; }
        if (Notification.permission === 'denied') { pushState.value = 'denied'; return; }
        if (Notification.permission === 'granted') { await subscribeToPushIfNeeded(); pushState.value = 'granted'; return; }
        pushState.value = 'prompt';
      } catch { pushState.value = 'unsupported'; }
    })();

    return () => clearInterval(iv);
  }, []);

  // Query param ?agent=wsId → selecionar agente automaticamente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentWsId = params.get('agent');
    if (agentWsId && agents.value.length > 0) {
      const found = agents.value.find(a => a.id === agentWsId || a.id === `ws-${agentWsId}`);
      if (found) {
        selectedAgent.value = found;
        // Limpar query param da URL sem recarregar
        const url = new URL(window.location.href);
        url.searchParams.delete('agent');
        window.history.replaceState({}, '', url.pathname);
      }
    }
  }, [agents.value.length]);

  // Sincronizar URL → activeConv (padrão ChatGPT: URL é a fonte de verdade)
  // Sincronizar URL → state
  // /chat/:id → carregar conversa | /agent/:agentId → selecionar agente
  useEffect(() => {
    if (id) {
      // Rota /chat/:id → carregar conversa
      const existing = conversations.value.find(c => c.id === id);
      if (existing) activeConv.value = existing;
      if (sending.value) return;
      const currentConvId = msgs.value[0]?.conversation_id;
      if (currentConvId !== id) msgs.value = [];
      fetchMessages(id);
    } else if (!agentId) {
      // Rota /chat (sem id, sem agentId) → tela limpa
      if (sending.value) return;
      activeConv.value = null;
      msgs.value = [];
    }
  }, [id]);

  // /agent/:agentId → selecionar agente, tela limpa
  useEffect(() => {
    if (agentId) {
      const found = agents.value.find(a => a.id === agentId);
      if (found) selectedAgent.value = found;
      if (!sending.value) {
        activeConv.value = null;
        msgs.value = [];
      }
    }
  }, [agentId, agents.value.length]);

  // Paste handler
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imgFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) { const f = item.getAsFile(); if (f) imgFiles.push(f); }
      }
      if (imgFiles.length > 0) { e.preventDefault(); addImages(imgFiles); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  // Close menus on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        attachMenuOpen.value = false;
        modelDropdownOpen.value = false;
        imageModal.value = null;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (msgs.value.length > prevLen.current) {
      if (isNearBottom.current || prevLen.current === 0) {
        setTimeout(() => scrollToBottom(prevLen.current === 0), 50);
      }
    }
    prevLen.current = msgs.value.length;
  }, [msgs.value.length]);

  const checkScroll = useCallback(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const near = (el.scrollHeight - el.scrollTop - el.clientHeight) < 150;
    isNearBottom.current = near;
    setShowScrollBtn(!near);
  }, []);

  const scrollToBottom = useCallback((instant?: boolean) => {
    const el = chatAreaRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'instant' as any : 'smooth' });
    setShowScrollBtn(false);
  }, []);

  const selectConversation = useCallback((conv: Conversation) => {
    route(`/chat/${conv.id}`);
    if (window.innerWidth < 768) sidebarOpen.value = false;
  }, []);

  const startNewConversation = useCallback(async (agent?: Agent) => {
    const a = agent || selectedAgent.value;
    if (a) selectedAgent.value = a;
    const aid = a?.id || '';
    const target = aid ? `/chat/agent/${aid}` : '/chat';
    // Se já está na página do agente, limpar sem navegar
    if (agentId === aid) {
      activeConv.value = null;
      msgs.value = [];
    } else {
      route(target);
    }
    if (window.innerWidth < 768) sidebarOpen.value = false;
  }, [agentId]);

  // Drag and drop
  const handleDragEnter = useCallback((e: DragEvent) => { e.preventDefault(); dragCounter.current++; dragOver.value = true; }, []);
  const handleDragOver = useCallback((e: DragEvent) => { e.preventDefault(); }, []);
  const handleDragLeave = useCallback((e: DragEvent) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current <= 0) { dragCounter.current = 0; dragOver.value = false; } }, []);
  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault(); dragCounter.current = 0; dragOver.value = false;
    if (!e.dataTransfer?.files?.length) return;
    const imgFiles: File[] = [];
    for (const file of Array.from(e.dataTransfer.files)) {
      if (ACCEPTED_TYPES.includes(file.type) && file.size <= MAX_IMAGE_SIZE && imgFiles.length < 4) imgFiles.push(file);
    }
    if (imgFiles.length > 0) addImages(imgFiles);
  }, []);

  // Balance badge color
  const balanceColor = balance.value === null ? COLORS.textMuted :
    balance.value > 5 ? COLORS.accentGreen :
    balance.value > 1 ? T.warning : T.danger;

  const filteredConvs = searchQuery.value
    ? localConvs.filter(c => (c.title || '').toLowerCase().includes(searchQuery.value.toLowerCase()))
    : localConvs;

  const onlineAgents = localAgents.filter(a => a.status === 'online');
  let lastDate = '';

  return (
    <div
      onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      style={{ display: 'flex', width: '100%', height: '100%', background: COLORS.bgPage, overflow: 'hidden', position: 'relative' }}
    >
      {/* Banner promocional */}
      <PromoBannerModal />
      {/* ===== SIDEBAR LEGADA REMOVIDA — sidebar principal (Sidebar.tsx) já mostra agentes+conversas ===== */}
      {false && (
        <>
          {/* Mobile overlay */}
          {window.innerWidth < 768 && (
            <div onClick={() => { sidebarOpen.value = false; }}
              style={{ position: 'fixed', inset: 0, background: T.bgOverlay, zIndex: 300, backdropFilter: 'blur(4px)' }} />
          )}
          <div style={{
            width: 300, minWidth: 300, height: '100%', display: 'flex', flexDirection: 'column',
            background: COLORS.bgSidebar, borderRight: `1px solid ${COLORS.border}`,
            ...(window.innerWidth < 768 ? { position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 301, animation: 'slideInLeft 0.2s ease-out' } : {}),
          }}>
            {/* Sidebar header */}
            <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>Chat Hub</h2>
                {window.innerWidth < 768 && (
                  <Button variant="ghost" onClick={() => { sidebarOpen.value = false; }}
                    style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 18, cursor: 'pointer', padding: 4 }}>X</Button>
                )}
              </div>
              {/* Search */}
              <Input
                placeholder="Buscar conversas..."
                value={searchQuery.value}
                onChange={(v) => { searchQuery.value = v; }}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 10, fontSize: 13,
                  background: T.bgCard,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Agents section */}
            <div style={{ padding: '12px 12px 8px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                Agentes
              </div>
              {onlineAgents.map(agent => (
                <Button variant="ghost"
                  key={agent.id}
                  onClick={() => startNewConversation(agent)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px',
                    borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: selectedAgent.value?.id === agent.id && !activeConv.value ? T.primaryBg : 'transparent',
                    color: COLORS.textPrimary, transition: 'background 0.15s', marginBottom: 2,
                  }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.background = T.bgCard; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.background = selectedAgent.value?.id === agent.id && !activeConv.value ? T.primaryBg : 'transparent'; }}
                >
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 10,
                      background: agent.type === 'elon_chat' ? `linear-gradient(135deg, ${T.primaryBg}, ${T.accentBg})` : T.successBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: agent.type === 'elon_chat' ? `1px solid ${T.primaryBgStrong}` : `1px solid ${T.success}`,
                    }}>
                      {agent.type === 'elon_chat' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="12" rx="2" stroke={T.success} strokeWidth="1.5"/><circle cx="9" cy="14" r="1.5" fill={T.success}/><circle cx="15" cy="14" r="1.5" fill={T.success}/><path d="M12 2v4M8 6h8" stroke={T.success} strokeWidth="1.5" strokeLinecap="round"/></svg>
                      )}
                    </div>
                    <div style={{
                      position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%',
                      background: agent.status === 'online' ? COLORS.accentGreen : T.textDisabled,
                      border: `2px solid ${COLORS.bgSidebar}`,
                    }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted }}>{agent.type === 'elon_chat' ? 'LLM direto' : 'Agente'}</div>
                  </div>
                </Button>
              ))}
            </div>

            {/* Conversations */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, padding: '8px 0 4px' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Conversas ({filteredConvs.length})
                </span>
              </div>
              {loadingConvs.value ? (
                <div style={{ padding: 20 }}><ListSkeleton rows={3} /></div>
              ) : filteredConvs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted, fontSize: 12 }}>Nenhuma conversa ainda</div>
              ) : filteredConvs.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 10, cursor: 'pointer', marginBottom: 2,
                    background: activeConv.value?.id === conv.id ? T.primaryBg : 'transparent',
                    border: activeConv.value?.id === conv.id ? `1px solid ${T.primaryBgStrong}` : '1px solid transparent',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e: any) => { if (activeConv.value?.id !== conv.id) e.currentTarget.style.background = T.bgCard; }}
                  onMouseLeave={(e: any) => { if (activeConv.value?.id !== conv.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: conv.type === 'elon_chat' ? T.primary : T.success,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {conv.title || 'Nova conversa'}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                      {conv.message_count} msgs · {formatTimeAgo(conv.updated_at)}
                    </div>
                  </div>
                  <Button variant="ghost"
                    onClick={(e) => { e.stopPropagation(); archiveConversation(conv.id); }}
                    style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 14, cursor: 'pointer', padding: 4, opacity: 0.5, flexShrink: 0 }}
                    title="Arquivar"
                  >X</Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ===== CHAT AREA ===== */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, background: COLORS.bgChat, overflow: 'hidden' }}>
        {/* Header desktop — estilo ChatGPT 1:1 */}
        {window.innerWidth >= 768 && (
        <div style={{
          padding: '0 16px', height: 48, borderBottom: `1px solid ${T.border}`,
          background: T.bg, display: 'flex', alignItems: 'center', flexShrink: 0,
          position: 'relative',
        }}>
          {/* Esquerda: nome do agente + modelo selecionado */}
          <div>
            <span style={{ fontSize: 18, fontWeight: 700, color: T.text, padding: '6px 12px' }}>
              {selectedAgent.value?.name || tenantName()}
              {(() => { const selModel = models.value.find(m => m.id === selectedModel.value); return selModel ? <span style={{ color: T.textSecondary, fontWeight: 400, fontSize: 14 }}> · {selModel.name}</span> : null; })()}
            </span>
          </div>

          {/* Espaçador flex */}
          <div style={{ flex: 1 }} />

          {/* Direita: perfil + nova conversa */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Ícone perfil */}
            <div onClick={() => route('/profile/wallet')} style={{
              width: 34, height: 34, minWidth: 34, minHeight: 34, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#2a2a2a', border: '1.5px solid rgba(255,255,255,0.25)', flexShrink: 0,
              boxSizing: 'border-box',
            }}
              onMouseEnter={(e: any) => { e.currentTarget.style.background = '#363636'; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.background = '#2a2a2a'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M20 21a8 8 0 00-16 0"/>
              </svg>
            </div>

            {/* Nova conversa — ícone + com borda (só aparece quando tem chat ativo) */}
            {(activeConv.value || msgs.value.length > 0) && (
            <div onClick={() => startNewConversation()} style={{
                width: 34, height: 34, minWidth: 34, minHeight: 34, borderRadius: '50%', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#2a2a2a', border: '1.5px solid rgba(255,255,255,0.25)',
                flexShrink: 0, boxSizing: 'border-box',
              }}
              onMouseEnter={(e: any) => { e.currentTarget.style.background = '#363636'; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.background = '#2a2a2a'; }}
              title="Nova conversa">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                <line x1="12" y1="8" x2="12" y2="14" />
                <line x1="9" y1="11" x2="15" y2="11" />
              </svg>
            </div>
            )}
          </div>
        </div>
        )}

        {/* Messages area */}
        {!activeConv.value && msgs.value.length === 0 ? (
          agentId && selectedAgent.value ? (
            /* ══ AGENT LANDING PAGE ══ */
            <AgentLandingView
              agent={selectedAgent.value}
              inputText={inputText}
              pendingImages={pendingImages}
              addImages={addImages}
              fileInputRef={fileInputRef}
              attachMenuOpen={attachMenuOpen}
              attachBtnRef={attachBtnRef}
              sending={sending}
              handleSend={handleSend}
              isListening={isListening}
              startListening={startListening}
              recognition={recognition}
              models={models}
              selectedModel={selectedModel}
              modelDropdownOpen={modelDropdownOpen}
            />
          ) :
          /* Empty state — estilo ChatGPT: tudo centralizado (titulo + input + pills) */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.bg, padding: '0 16px' }}>
            <span style={{ fontSize: 28, fontWeight: 600, color: T.text, textAlign: 'center', marginBottom: 24, minHeight: '1.2em' }}>
              {greetingText}<span style={{ borderRight: `2px solid ${T.text}`, marginLeft: 1, animation: 'blink 1s step-end infinite' }} />
            </span>

            {/* Input bar centralizado no empty state */}
            <div style={{ width: '100%', maxWidth: window.innerWidth >= 768 ? 860 : '100%' }}>
              {/* Pending images */}
              {pendingImages.value.length > 0 && (
                <div style={{ padding: '8px 12px', background: COLORS.bgSidebar, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', gap: 8, marginBottom: 8, borderRadius: 12 }}>
                  {pendingImages.value.map((img: any, idx: number) => (
                    <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                      <img src={img.dataUrl} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `2px solid ${COLORS.border}` }} />
                      <Button variant="ghost" onClick={() => { pendingImages.value = pendingImages.value.filter((_: any, i: number) => i !== idx); }}
                        style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%', background: T.danger, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.text }}>x</Button>
                    </div>
                  ))}
                </div>
              )}
              {/* Pill container — textarea + botões em linha única */}
              <div style={{ background: T.bgInput, borderRadius: 24, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 8px 4px', gap: 4 }}>
                  <AttachMenu menuOpen={attachMenuOpen} fileInputRef={fileInputRef} btnRef={attachBtnRef} />
                  <textarea
                    data-chathub-input
                    placeholder="Pergunte alguma coisa"
                    value={inputText.value}
                    onFocus={(e: any) => { setTimeout(() => { e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 300); }}
                    onInput={(e: any) => {
                      inputText.value = e.target.value;
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                    onKeyDown={(e: KeyboardEvent) => {
                      const isMobile = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
                      if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(); }
                    }}
                    disabled={sending.value}
                    rows={1}
                    style={{
                      flex: 1, padding: '0 8px', borderRadius: 0,
                      background: 'transparent', border: 'none',
                      fontSize: 16, lineHeight: 1.5, color: T.text,
                      fontFamily: 'inherit', outline: 'none', resize: 'none',
                      maxHeight: 120, overflowY: 'auto',
                    }}
                  />
                  {window.innerWidth >= 768 && (
                    <ModelSelector models={models} selectedModel={selectedModel} dropdownOpen={modelDropdownOpen} />
                  )}
                  <Button variant="ghost" onClick={() => { if (!isListening.value) startListening(false); else if (recognition) recognition.stop(); }}
                    class="chathub-mic-btn"
                    style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', display: window.innerWidth > 768 ? 'flex' : (!inputText.value.trim() ? 'flex' : 'none'), alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: isListening.value ? T.danger : T.textSecondary, padding: 0 }}
                    onMouseEnter={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.text; }}
                    onMouseLeave={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.textSecondary; }}>
                    <Icon name="microphone" size={18} />
                  </Button>
                  <Button variant="ghost" data-chathub-send onClick={handleSend}
                    disabled={(!inputText.value.trim() && pendingImages.value.length === 0) || sending.value}
                    style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', padding: 0, background: (inputText.value.trim() || pendingImages.value.length > 0) && !sending.value ? T.text : T.textMuted, transition: 'all 0.2s' }}>
                    <Icon name="arrow-up" size={14} color="#000" />
                  </Button>
                </div>
              </div>
              {/* Pills */}
              {window.innerWidth >= 768 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {/* Criar Agente — com badge NOVO flutuante */}
                  <div style={{ position: 'relative' }}>
                    <Button variant="ghost"
                      onClick={() => { route('/agentes'); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: `1px solid ${T.borderHover}`, background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}>
                      <Icon name="user-plus" size={16} />
                      Criar Agente
                    </Button>
                    <span style={{ position: 'absolute', top: -6, right: -8, fontSize: 8, fontWeight: 700, background: T.warning, color: T.text, padding: '2px 5px', borderRadius: 6, letterSpacing: 0.5, lineHeight: '12px', pointerEvents: 'none' }}>NOVO</span>
                  </div>
                  {[
                    { label: 'Crie imagens', prompt: 'Crie uma imagem de ', iconName: 'image' },
                    { label: 'Escreva ou edite', prompt: 'Escreva ', iconName: 'edit' },
                    { label: 'Consulte algo', prompt: 'Pesquise sobre ', iconName: 'globe' },
                  ].map(pill => (
                    <Button variant="ghost" key={pill.label}
                      onClick={() => { inputText.value = pill.prompt; const ta = document.querySelector('[data-chathub-input]') as HTMLTextAreaElement; if (ta) { ta.focus(); ta.setSelectionRange(pill.prompt.length, pill.prompt.length); } }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: `1px solid ${T.borderHover}`, background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}>
                      <Icon name={pill.iconName} size={16} />
                      {pill.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div
              ref={chatAreaRef} onScroll={checkScroll}
              style={{
                flex: 1, overflowY: 'auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 6,
                minHeight: 0, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple style={{ display: 'none' }}
                onChange={(e: any) => { if (e.target.files) addImages(e.target.files); e.target.value = ''; }} />

              {loadingMsgs.value ? (
                <div style={{ padding: 60, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500, margin: '0 auto', width: '100%' }}>
                  <ListSkeleton rows={4} />
                </div>
              ) : msgs.value.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200 }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: T.text, minHeight: '1.2em' }}>
                    {greetingText}<span style={{ borderRight: `2px solid ${T.text}`, marginLeft: 1, animation: 'blink 1s step-end infinite' }} />
                  </div>
                </div>
              ) : msgs.value.map((msg, i) => {
                const isUser = msg.role === 'user';
                const dateLabel = formatDate(msg.created_at);
                let showDate = false;
                if (dateLabel !== lastDate) { lastDate = dateLabel; showDate = true; }

                // Tool data rendering
                let toolCalls: any[] = [];
                if (msg.tool_data) {
                  try { toolCalls = JSON.parse(msg.tool_data); } catch {}
                }

                return (
                  <div key={msg.id || i}>
                    {showDate && (
                      <div style={{ textAlign: 'center', margin: '14px 0 8px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: COLORS.textMuted,
                          background: T.bgCard, padding: '4px 14px', borderRadius: 12,
                          border: `1px solid ${COLORS.border}`,
                        }}>{dateLabel}</span>
                      </div>
                    )}

                    {/* Tool calls (collapsible) */}
                    {showActions.value && toolCalls.length > 0 && toolCalls.map((tc: any, ti: number) => {
                      const meta = toolMeta(tc.function?.name || tc.name || 'unknown');
                      return (
                        <div key={ti} style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 12px', margin: '2px 0' }}>
                          <div style={{
                            maxWidth: '85%', borderRadius: 10, padding: '6px 12px',
                            background: `${meta.color}08`, border: `1px solid ${meta.color}18`,
                            display: 'flex', alignItems: 'center', gap: 8,
                          }}>
                            <span style={{
                              width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: `${meta.color}12`, fontSize: 10, fontWeight: 700, color: meta.color,
                            }}>T</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                            <span style={{ fontSize: 9, color: COLORS.textMuted }}>{formatTime(msg.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Message bubble */}
                    <div style={{
                      display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
                      padding: '0 12px', animation: i >= msgs.value.length - 3 ? 'msgIn 0.2s ease-out' : 'none',
                    }}>
                      <div style={{
                        maxWidth: '80%', padding: '10px 14px',
                        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        background: isUser ? `linear-gradient(135deg, ${T.primary}, ${T.accent})` : COLORS.assistantBubble,
                        border: isUser ? 'none' : `1px solid ${COLORS.border}`,
                        color: COLORS.textPrimary,
                      }}>
                        {/* Attachments */}
                        {msg.attachments && (() => {
                          try {
                            const atts = JSON.parse(msg.attachments);
                            return atts.map((att: any, ai: number) => (
                              <img key={ai} src={att.dataUrl} style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 10, objectFit: 'contain', cursor: 'pointer', display: 'block', marginBottom: 6 }}
                                onClick={() => { imageModal.value = att.dataUrl; }} />
                            ));
                          } catch { return null; }
                        })()}

                        {/* Content */}
                        {msg.content ? (
                          <div style={{ fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word' }}
                            dangerouslySetInnerHTML={{ __html: renderContent(msg.content) }} />
                        ) : !isUser ? (
                          <TypingIndicator />
                        ) : null}

                        {/* Timestamp + meta */}
                        <div style={{
                          fontSize: 10, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6,
                          color: isUser ? T.textMuted : COLORS.textMuted,
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                        }}>
                          <span>{formatTime(msg.created_at)}</span>
                          {msg.id.startsWith('opt_') && <span style={{ fontSize: 11 }}>...</span>}
                          {!isUser && msg.tokens_in != null && (
                            <span style={{ fontSize: 9, opacity: 0.6 }}>
                              {msg.tokens_in}+{msg.tokens_out} tokens
                              {msg.cost_usd != null && ` · $${msg.cost_usd.toFixed(4)}`}
                              {msg.duration_ms != null && ` · ${(msg.duration_ms / 1000).toFixed(1)}s`}
                            </span>
                          )}
                        </div>

                        {/* Botão CTA para recarga quando saldo insuficiente */}
                        {msg.content && (msg.content.includes('saldo') || msg.content.includes('Saldo')) && !isUser && (
                          <Button variant="primary" size="md" onClick={() => { showRechargeModal.value = true; }}
                            style={{ marginTop: 10, borderRadius: 12, display: 'block', width: '100%', fontWeight: 700, fontSize: 14, padding: '12px 20px' }}>
                            Adicionar Créditos
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Sending indicator */}
              {sending.value && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 12px' }}>
                  <div style={{ padding: '10px 18px', borderRadius: '18px 18px 18px 4px', background: COLORS.assistantBubble, border: `1px solid ${COLORS.border}` }}>
                    <TypingIndicator />
                  </div>
                </div>
              )}
            </div>

            {/* Scroll to bottom */}
            {showScrollBtn && (
              <div style={{ position: 'absolute', bottom: 80, right: 20, zIndex: 50 }}>
                <Button variant="ghost" onClick={() => scrollToBottom()} style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`, border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 4px 20px ${T.primary}`,
                }}>
                  <Icon name="chevron-down" size={18} color="#fff" />
                </Button>
              </div>
            )}
          </>
        )}

            {/* Input bar + pending images — só quando tem conversa ativa */}
            {(activeConv.value || msgs.value.length > 0) && pendingImages.value.length > 0 && (
              <div style={{ padding: '8px 12px', background: COLORS.bgSidebar, borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 8, flexShrink: 0 }}>
                {pendingImages.value.map((img, idx) => (
                  <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={img.dataUrl} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `2px solid ${COLORS.border}` }} />
                    <Button variant="ghost" onClick={() => { pendingImages.value = pendingImages.value.filter((_, i) => i !== idx); }}
                      style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%', background: T.danger, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.text }}>x</Button>
                  </div>
                ))}
              </div>
            )}

            {/* Input bar — só quando tem conversa ativa (no empty state fica centralizado acima) */}
            {(activeConv.value || msgs.value.length > 0) && <div style={{ padding: '8px 12px', background: T.bg, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Container centralizado — max-width 700px no desktop */}
              <div style={{ width: '100%', maxWidth: window.innerWidth >= 768 ? 860 : '100%' }}>
                {/* Pill container — textarea + botões */}
                <div style={{
                  background: T.bgInput, borderRadius: 24, overflow: 'hidden',
                }}>
                  {/* Input row — tudo em uma linha: ➕ textarea 🎤 ▶ */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 8px 4px', gap: 4 }}>
                    {/* Botão + anexar */}
                    <AttachMenu menuOpen={attachMenuOpen} fileInputRef={fileInputRef} btnRef={attachBtnRef} />

                    <textarea
                      data-chathub-input
                      placeholder="Pergunte alguma coisa"
                      value={inputText.value}
                      onFocus={(e: any) => {
                        setTimeout(() => { e.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 300);
                      }}
                      onInput={(e: any) => {
                        inputText.value = e.target.value;
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                      onKeyDown={(e: KeyboardEvent) => {
                        const isMobile = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
                        if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); handleSend(); }
                      }}
                      disabled={sending.value}
                      rows={1}
                      style={{
                        flex: 1, padding: '0 8px', borderRadius: 0,
                        background: 'transparent', border: 'none',
                        fontSize: 16, lineHeight: '24px', height: 24, color: T.text,
                        fontFamily: 'inherit', outline: 'none', resize: 'none',
                        maxHeight: 120, overflowY: 'auto',
                      }}
                    />

                    {/* Model selector inline — desktop only */}
                    {window.innerWidth >= 768 && (
                      <ModelSelector models={models} selectedModel={selectedModel} dropdownOpen={modelDropdownOpen} />
                    )}

                    {/* Mic button */}
                    <Button variant="ghost" onClick={() => { if (!isListening.value) startListening(false); else if (recognition) recognition.stop(); }}
                      class="chathub-mic-btn"
                      style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                        display: window.innerWidth > 768 ? 'flex' : (!inputText.value.trim() ? 'flex' : 'none'),
                        alignItems: 'center', justifyContent: 'center',
                        background: 'transparent', border: 'none',
                        color: isListening.value ? T.danger : T.textSecondary, padding: 0,
                      }}
                      onMouseEnter={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.text; }}
                      onMouseLeave={(e: any) => { if (!isListening.value) e.currentTarget.style.color = T.textSecondary; }}
                    >
                      <Icon name="microphone" size={18} />
                    </Button>

                    {/* Enviar — botão circular */}
                    <Button variant="ghost"
                      data-chathub-send
                      onClick={handleSend}
                      disabled={(!inputText.value.trim() && pendingImages.value.length === 0) || sending.value}
                      style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none',
                        padding: 0,
                        background: (inputText.value.trim() || pendingImages.value.length > 0) && !sending.value
                          ? T.text : T.textMuted,
                        transition: 'all 0.2s',
                      }}>
                      <Icon name="arrow-up" size={14} color="#000" />
                    </Button>
                  </div>
                </div>

                {/* Pills abaixo — só no empty state desktop */}
                {!activeConv.value && msgs.value.length === 0 && window.innerWidth >= 768 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {/* Criar Agente — com badge NOVO flutuante */}
                    <div style={{ position: 'relative' }}>
                      <Button variant="ghost"
                        onClick={() => { route('/agentes'); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: `1px solid ${T.borderHover}`, background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}>
                        <Icon name="user-plus" size={16} />
                        Criar Agente
                      </Button>
                      <span style={{ position: 'absolute', top: -6, right: -8, fontSize: 8, fontWeight: 700, background: T.warning, color: T.text, padding: '2px 5px', borderRadius: 6, letterSpacing: 0.5, lineHeight: '12px', pointerEvents: 'none' }}>NOVO</span>
                    </div>
                    {[
                      { label: 'Crie imagens', prompt: 'Crie uma imagem de ', iconName: 'image' },
                      { label: 'Escreva ou edite', prompt: 'Escreva ', iconName: 'edit' },
                      { label: 'Consulte algo', prompt: 'Pesquise sobre ', iconName: 'globe' },
                    ].map(pill => (
                      <Button variant="ghost"
                        key={pill.label}
                        onClick={() => {
                          inputText.value = pill.prompt;
                          const ta = document.querySelector('[data-chathub-input]') as HTMLTextAreaElement;
                          if (ta) { ta.focus(); ta.setSelectionRange(pill.prompt.length, pill.prompt.length); }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                          borderRadius: 20, border: `1px solid ${T.borderHover}`,
                          background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <Icon name={pill.iconName} size={16} />
                        {pill.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>}
      </div>

      {/* Voice overlay */}
      {(isListening.value || isSpeaking.value) && (
        <div style={{ position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', zIndex: 60 }}>
          <div style={{
            padding: '10px 20px', borderRadius: 18,
            background: isJarvisMode.value ? T.dangerBg : isSpeaking.value ? T.successBg : T.dangerBg,
            border: `1px solid ${isJarvisMode.value ? T.danger : isSpeaking.value ? T.success : T.danger}`,
            backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            {isSpeaking.value ? (
              <>
                <span style={{ color: T.success, fontSize: 13 }}>Falando...</span>
                <Button variant="danger" size="xs" onClick={stopSpeaking}>Parar</Button>
              </>
            ) : (
              <>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.danger, animation: 'pulse 1s ease-in-out infinite' }} />
                <div>
                  <div style={{ color: isJarvisMode.value ? T.warning : T.danger, fontSize: 12, fontWeight: 600 }}>
                    {isJarvisMode.value ? 'Jarvis Mode' : 'Ouvindo...'}
                  </div>
                  {voiceTranscript.value && <div style={{ color: COLORS.textPrimary, fontSize: 13, marginTop: 2, maxWidth: 280 }}>"{voiceTranscript.value}"</div>}
                </div>
                {isJarvisMode.value && <Button variant="danger" size="xs" onClick={stopListening}>Desligar</Button>}
              </>
            )}
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {imageModal.value && (
        <div onClick={() => { imageModal.value = null; }} style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: T.bgOverlay,
          display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', cursor: 'pointer',
        }}>
          <Button variant="ghost" onClick={() => { imageModal.value = null; }} style={{
            position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: '50%',
            background: T.borderHover, border: 'none', color: T.text, fontSize: 20, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>X</Button>
          <img src={imageModal.value} onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '90vw', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain', cursor: 'default',
          }} />
        </div>
      )}

      {/* Drag overlay */}
      {dragOver.value && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 100,
          background: 'rgba(11,15,26,0.85)', border: `3px dashed ${T.primaryBgStrong}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{ background: T.primaryBg, border: `2px dashed ${T.primaryBgStrong}`, padding: '40px 60px', borderRadius: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 600, color: T.text }}>Solte o arquivo aqui</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Imagens (JPG, PNG, GIF, WebP)</div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes blink { 50% { border-color: transparent; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes msgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Modal de Recarga */}
      <RechargeModal />
    </div>
  );
}

// ---- Modal de Recarga ----
// Usa RechargeModal de ../components/ui

function RechargeModal() {
  return <UIRechargeModal showModal={showRechargeModal} balance={balance} api={api} />;
}

/* === RechargeModal local removido — versão ui/ é canônica === */

// Wrapper to bypass @preact/signals shouldComponentUpdate interference
// The inner component uses both signals and useState, which causes
// shouldComponentUpdate to block re-renders when only useState changes
import { Component } from 'preact';
export class ChatHubPage extends Component<{ id?: string; agentId?: string; path?: string }> {
  shouldComponentUpdate() { return true; }
  render() { return <ChatHubPageInner id={this.props.id} agentId={this.props.agentId} />; }
}
